import { createContext, useContext, useReducer, useEffect, useRef, useCallback, useMemo } from 'react';

const CartContext = createContext();

// Crear un EventEmitter para los cambios en el carrito
const cartEventEmitter = {
  listeners: new Map(),
  globalListeners: new Set(),
  
  subscribe(productId, callback) {
    if (!this.listeners.has(productId)) {
      this.listeners.set(productId, new Set());
    }
    this.listeners.get(productId).add(callback);
    
    return () => {
      const listeners = this.listeners.get(productId);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          this.listeners.delete(productId);
        }
      }
    };
  },
  
  subscribeToAll(callback) {
    this.globalListeners.add(callback);
    
    return () => {
      this.globalListeners.delete(callback);
    };
  },
  
  emit(productId) {
    // Notificar a los oyentes específicos del producto
    const listeners = this.listeners.get(productId);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback();
        } catch (error) {
          console.error('Error en listener del carrito:', error);
        }
      });
    }
    
    // Notificar a los oyentes globales
    this.globalListeners.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error en listener global del carrito:', error);
      }
    });
  },
  
  // Notificar a todos los oyentes (para cambios que afectan a todos los productos)
  emitAll() {
    // Notificar a todos los productos con oyentes
    this.listeners.forEach((_, productId) => {
      this.emit(productId);
    });
    
    // Notificar a los oyentes globales
    this.globalListeners.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error en listener global del carrito:', error);
      }
    });
  }
};

const initialState = {
  items: [],
  totalItems: 0
};

// Crear estado inicial con verificación de localStorage
const getInitialState = () => {
  if (typeof window !== 'undefined') {
    try {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        // Verificar que el carrito tenga la estructura correcta
        if (parsedCart && Array.isArray(parsedCart.items)) {
          return parsedCart;
        }
      }
    } catch (error) {
      console.error('Error al cargar el carrito desde localStorage:', error);
    }
  }
  return {
    items: [],
    totalItems: 0
  };
};

function cartReducer(state, action) {
  switch (action.type) {
    case 'ADD_OR_UPDATE_ITEM': {
      const { product, quantity, selectedFormat } = action.payload;
      const existingItemIndex = state.items.findIndex(item => item.id === product.id);

      if (existingItemIndex >= 0) {
        // Actualizar cantidad de un producto existente
        const updatedItems = [...state.items];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          bulkQuantity: quantity,
          selectedFormat: selectedFormat || updatedItems[existingItemIndex].selectedFormat
        };
        return {
          ...state,
          items: updatedItems
        };
      }

      // Añadir nuevo producto
      return {
        ...state,
        items: [...state.items, { 
          ...product, 
          bulkQuantity: quantity,
          selectedFormat: selectedFormat,
          // Asegurarnos de que countPerBottle esté disponible en el carrito
          countPerBottle: product.countPerBottle || []
        }],
        totalItems: state.totalItems + 1
      };
    }

    case 'REMOVE_ITEM': {
      const productId = action.payload;
      cartEventEmitter.emit(productId);
      
      return {
        ...state,
        items: state.items.filter(item => item.id !== productId),
        totalItems: state.totalItems - 1
      };
    }

    case 'CLEAR_CART':
      state.items.forEach(item => cartEventEmitter.emit(item.id));
      return initialState;

    case 'LOAD_CART':
      return action.payload;

    default:
      return state;
  }
}

export function CartProvider({ children }) {
  // Usar función para inicializar estado desde localStorage
  const [state, dispatch] = useReducer(cartReducer, getInitialState());
  const prevStateRef = useRef(null);
  
  // Ya no necesitamos cargar desde localStorage aquí porque lo hacemos en la inicialización

  // Guardar carrito en localStorage y emitir eventos
  useEffect(() => {
    // Comparar con el estado anterior para determinar qué cambió
    if (state.items !== prevStateRef.current?.items) {
      // Identificar qué productos cambiaron
      const prevItems = prevStateRef.current?.items || [];
      const currentIds = new Set(state.items.map(item => item.id));
      const prevIds = new Set(prevItems.map(item => item.id));
      
      // Productos que se añadieron o actualizaron
      state.items.forEach(item => {
        const prevItem = prevItems.find(prev => prev.id === item.id);
        if (!prevItem || 
            prevItem.bulkQuantity !== item.bulkQuantity || 
            prevItem.selectedFormat !== item.selectedFormat) {
          cartEventEmitter.emit(item.id);
        }
      });
      
      // Productos que se eliminaron
      prevItems.forEach(item => {
        if (!currentIds.has(item.id)) {
          cartEventEmitter.emit(item.id);
        }
      });
      
      // Si cambió el total de items, notificar a todos
      if (state.totalItems !== prevStateRef.current?.totalItems) {
        cartEventEmitter.emitAll();
      }
    }
    
    // Actualizar la referencia al estado anterior
    prevStateRef.current = state;
    
    // Guardar en localStorage solo si hay items
    try {
      if (state.items && state.items.length > 0) {
        console.log('Guardando carrito en localStorage:', state);
        localStorage.setItem('cart', JSON.stringify(state));
      } else if (state.items && state.items.length === 0) {
        // Si el carrito está vacío, también actualizamos localStorage
        localStorage.setItem('cart', JSON.stringify(state));
      }
    } catch (error) {
      console.error('Error al guardar el carrito en localStorage:', error);
    }
  }, [state]);

  // Add or update an item in the cart
  // Añadir o actualizar un artículo en el carrito
  const addOrUpdateItem = useCallback((product, quantity, selectedFormat = null) => {
    return new Promise((resolve, reject) => {
      // Validate product data before dispatching
      if (!product) {
        console.error('Error: No product provided to addOrUpdateItem');
        reject(new Error('No product provided'));
        return;
      }
      
      if (!product.id) {
        console.error('Error: Invalid product data - missing ID', product);
        reject(new Error('Invalid product data - missing ID'));
        return;
      }
      
      if (!quantity || quantity <= 0) {
        console.error('Error: Invalid quantity provided', quantity);
        reject(new Error('Invalid quantity'));
        return;
      }
      
      try {
        dispatch({ type: 'ADD_OR_UPDATE_ITEM', payload: { product, quantity, selectedFormat } });
        
        // Use setTimeout to ensure state is updated before resolving the promise
        // Usar setTimeout para asegurar que el estado se actualice antes de resolver la promesa
        setTimeout(() => {
          resolve();
        }, 0);
      } catch (error) {
        console.error('Error in cart dispatch:', error);
        reject(error);
      }
    });
  }, []);

  // Remove an item from the cart
  // Eliminar un artículo del carrito
  const removeItem = useCallback((productId) => {
    dispatch({ type: 'REMOVE_ITEM', payload: productId });
  }, []);

  // Clear the entire cart
  // Vaciar todo el carrito
  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
  }, []);

  // Check if an item is in the cart
  // Verificar si un artículo está en el carrito
  const isItemInCart = useCallback((productId) => {
    return state.items.some(item => item.id === productId);
  }, [state.items]);

  // Get the quantity of an item in the cart
  // Obtener la cantidad de un artículo en el carrito
  const getItemQuantity = useCallback((productId) => {
    const item = state.items.find(item => item.id === productId);
    return item ? item.bulkQuantity : null;
  }, [state.items]);

  // Subscribe to changes for a specific product
  // Suscribirse a cambios para un producto específico
  const subscribeToCartChanges = useCallback((productId, callback) => {
    return cartEventEmitter.subscribe(productId, callback);
  }, []);

  // Subscribe to all cart changes
  // Suscribirse a todos los cambios del carrito
  const subscribeToAllCartChanges = useCallback((callback) => {
    return cartEventEmitter.subscribeToAll(callback);
  }, []);

  // Memoize the context value to prevent unnecessary re-renders of consumers
  // Memorizar el valor del contexto para evitar re-renderizados innecesarios de los consumidores
  const value = useMemo(() => ({
    items: state.items,
    totalItems: state.totalItems,
    addOrUpdateItem,
    removeItem,
    clearCart,
    isItemInCart,
    getItemQuantity,
    subscribeToCartChanges,
    subscribeToAllCartChanges
  }), [
    state.items,
    state.totalItems,
    addOrUpdateItem,
    removeItem,
    clearCart,
    isItemInCart,
    getItemQuantity,
    subscribeToCartChanges,
    subscribeToAllCartChanges
  ]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

/**
 * Custom hook to use the cart context
 * Hook personalizado para usar el contexto del carrito
 * 
 * @returns {Object} Cart context value / Valor del contexto del carrito
 * @throws {Error} If used outside of CartProvider / Si se usa fuera de CartProvider
 */
export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
